/**
 * Buyer Claim landing page (Phase 5C).
 *
 * Mounted at `/claim/:token`. Public, unauthenticated. The realtor
 * dispatched this URL to the buyer's phone after marking a listing
 * sold; the buyer arrives, sees the listing preview, taps "Send OTP"
 * (sent to the phone the realtor entered — non-transferable), enters
 * the OTP + their name, and the page hands them off into the
 * homeowner dashboard with their tokens stored.
 *
 * Backend at `GET /api/v1/buyer-handoffs/token/:token` returns 404 on
 * any unpublished/expired/onboarded slug — same existence-leak posture
 * as the public listing surface. We map that to a single not-found
 * branch on the frontend.
 */

import { CheckCircle2, Home, Loader2, MapPin, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	claimHandoff,
	getPublicHandoff,
	type PublicHandoffPreview,
	sendClaimOtp,
} from "@/features/realtor/services/buyer-handoffs-api.service";
import { formatPaiseAsRupees } from "@/features/realtor/utils/listings-format";
import { StudioButton } from "@/features/studio/theme";
import { clearTokens, markSignedIn } from "@/lib/token-manager";
import { resolveMediaUrl } from "@/lib/utils";

type LoadState =
	| { status: "loading" }
	| { status: "not-found" }
	| { status: "error"; message: string }
	| { status: "loaded"; preview: PublicHandoffPreview };

type ClaimStep = "preview" | "verify";

export function BuyerClaimPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = useParams<{ token: string }>();
	const setUser = useAuthStore((state) => state.setUser);
	const [load, setLoad] = useState<LoadState>({ status: "loading" });
	const [step, setStep] = useState<ClaimStep>("preview");
	const [otp, setOtp] = useState("");
	const [homeownerName, setHomeownerName] = useState("");
	const [otpInFlight, setOtpInFlight] = useState(false);
	const [claimInFlight, setClaimInFlight] = useState(false);
	const unmountedRef = useRef(false);

	const runLoad = useCallback(async () => {
		if (!token) {
			setLoad({ status: "not-found" });
			return;
		}
		setLoad({ status: "loading" });
		const result = await getPublicHandoff(token);
		if (unmountedRef.current) return;
		if (!result.success) {
			if (result.status === "not-found") {
				setLoad({ status: "not-found" });
			} else {
				setLoad({ status: "error", message: result.message });
			}
			return;
		}
		setLoad({ status: "loaded", preview: result.preview });
	}, [token]);

	useEffect(() => {
		unmountedRef.current = false;
		runLoad();
		return () => {
			unmountedRef.current = true;
		};
	}, [runLoad]);

	const handleSendOtp = async () => {
		if (!token) return;
		setOtpInFlight(true);
		const result = await sendClaimOtp(token);
		if (unmountedRef.current) return;
		setOtpInFlight(false);
		if (!result.success) {
			// 404 — link expired or already onboarded between preview and
			// send-otp. Switch to the not-found UI so the buyer sees the
			// same posture they would on a fresh expired-link visit.
			if (result.status === "not-found") {
				setLoad({ status: "not-found" });
				return;
			}
			toast.error(result.message ?? t("realtor.claim.otpSendFailed"));
			return;
		}
		toast.success(t("realtor.claim.otpSentToast"));
		setStep("verify");
	};

	const handleClaim = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!token) return;
		const trimmedOtp = otp.trim();
		const trimmedName = homeownerName.trim();
		if (!/^\d{4,8}$/.test(trimmedOtp)) {
			toast.error(t("realtor.claim.otpInvalid"));
			return;
		}
		if (!trimmedName) {
			toast.error(t("realtor.claim.nameRequired"));
			return;
		}
		setClaimInFlight(true);
		const result = await claimHandoff(token, {
			otp: trimmedOtp,
			homeownerName: trimmedName,
		});
		if (unmountedRef.current) return;
		setClaimInFlight(false);
		if (!result.success) {
			// Same not-found posture as sendClaimOtp — the link could have
			// been claimed by a parallel session in between.
			if (result.status === "not-found") {
				setLoad({ status: "not-found" });
				return;
			}
			toast.error(result.message ?? t("realtor.claim.claimFailed"));
			return;
		}
		// Persist tokens via the same token manager the OTP login flow
		// uses, then push the user into the auth store. The backend
		// returns a public-safe minimal user shape (id/phone/userType/
		// languagePreference), so we cast through unknown to satisfy
		// the FinishdUser shape; the auth store hydrates the full user
		// via the next /me call after we land on /dashboard.
		//
		// Order matters: if setUser fails (Zustand persist write failure
		// in incognito / private mode), we must clear the tokens we just
		// stashed so the user isn't left half-authed on next reload.
		// Auth cookies (access + session) are set by the claim response; just
		// record that this device now has a session.
		markSignedIn();
		try {
			setUser({
				...result.data.user,
				isNewUser: true,
			} as unknown as Parameters<typeof setUser>[0]);
		} catch (err) {
			clearTokens();
			console.error("[BuyerClaim] setUser failed; rolled back tokens", err);
			toast.error(t("realtor.claim.claimFailed"));
			return;
		}
		toast.success(t("realtor.claim.welcomeToast"));
		navigate("/dashboard");
	};

	if (load.status === "loading") {
		return (
			<div
				className="bg-dashboard-surface min-h-screen p-6 space-y-6 max-w-md mx-auto"
				data-testid="buyer-claim-loading"
			>
				<Skeleton className="h-32 w-full" />
				<Skeleton className="h-48 w-full" />
			</div>
		);
	}

	if (load.status === "not-found") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3">
						<h1
							className="text-lg font-semibold"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							{t("realtor.claim.notFoundTitle")}
						</h1>
						<p
							className="text-sm"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{t("realtor.claim.notFoundBody")}
						</p>
						<StudioButton variant="secondary" onClick={() => navigate("/")}>
							{t("realtor.claim.backHome")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card
					className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]"
					style={{ borderColor: "var(--color-dashboard-pill-risk)" }}
				>
					<CardContent className="p-6 text-center space-y-3" role="alert">
						<p
							className="text-sm"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{load.message}
						</p>
						<StudioButton variant="secondary" onClick={runLoad}>
							{t("realtor.claim.retry")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	const { preview } = load;
	const cover = preview.listing.images[0];

	return (
		<div className="bg-dashboard-surface min-h-screen pb-12 max-w-md mx-auto">
			<header className="px-5 pt-8 pb-3 text-center">
				<div
					className="inline-flex h-10 w-10 items-center justify-center rounded-full mb-2"
					style={{ backgroundColor: "var(--color-dashboard-pill-awaiting-bg)" }}
				>
					<Home
						className="h-5 w-5"
						style={{ color: "var(--color-dashboard-accent-dark)" }}
					/>
				</div>
				<h1
					className="text-xl font-semibold"
					style={{ color: "var(--color-dashboard-text)" }}
				>
					{t("realtor.claim.heroTitle")}
				</h1>
				<p
					className="text-sm mt-1"
					style={{ color: "var(--color-dashboard-muted)" }}
				>
					{t("realtor.claim.heroSubtitle", { realtor: preview.realtor.name })}
				</p>
			</header>

			{/* Listing preview */}
			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-0 overflow-hidden">
					{cover && (
						<div
							className="aspect-[4/3] overflow-hidden"
							style={{ backgroundColor: "var(--color-dashboard-surface)" }}
						>
							<img
								src={resolveMediaUrl(cover)}
								alt={preview.listing.headline}
								className="w-full h-full object-cover"
							/>
						</div>
					)}
					<div className="p-4 space-y-1">
						<p
							className="font-semibold"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							{preview.listing.headline}
						</p>
						<p
							className="text-xs inline-flex items-center gap-1"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							<MapPin className="h-3 w-3" />
							{preview.listing.locality}, {preview.listing.city}
						</p>
						<p
							className="text-sm font-semibold pt-1"
							style={{ color: "var(--color-dashboard-accent-dark)" }}
						>
							{formatPaiseAsRupees(preview.listing.askingPrice)}
						</p>
						{preview.realtor.agencyName && (
							<p
								className="text-[11px] inline-flex items-center gap-1 pt-1"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								<ShieldCheck className="h-3 w-3" />
								{preview.realtor.name} · {preview.realtor.agencyName}
							</p>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Step 1: send OTP */}
			{step === "preview" && (
				<Card className="mt-4 bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-5 space-y-3">
						<p
							className="text-sm"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							{t("realtor.claim.otpPrompt", {
								phone: preview.buyerPhoneMasked,
							})}
						</p>
						<StudioButton
							variant="primary"
							className="w-full gap-1.5"
							onClick={handleSendOtp}
							disabled={otpInFlight}
						>
							{otpInFlight && <Loader2 className="h-4 w-4 animate-spin" />}
							{t("realtor.claim.sendOtp")}
						</StudioButton>
					</CardContent>
				</Card>
			)}

			{/* Step 2: verify + name */}
			{step === "verify" && (
				<form onSubmit={handleClaim}>
					<Card className="mt-4 bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
						<CardContent className="p-5 space-y-4">
							<div className="space-y-1.5">
								<Label htmlFor="otp">{t("realtor.claim.otpLabel")}</Label>
								<Input
									id="otp"
									type="tel"
									inputMode="numeric"
									maxLength={8}
									value={otp}
									onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
									placeholder="123456"
									autoComplete="one-time-code"
									className="border-dashboard-border bg-dashboard-bg text-dashboard-text"
								/>
								<button
									type="button"
									onClick={handleSendOtp}
									disabled={otpInFlight}
									className="text-[11px] hover:underline disabled:opacity-60"
									style={{ color: "var(--color-dashboard-accent-dark)" }}
								>
									{otpInFlight
										? t("realtor.claim.resending")
										: t("realtor.claim.resendOtp")}
								</button>
							</div>
							<div className="space-y-1.5">
								<Label htmlFor="homeowner-name">
									{t("realtor.claim.nameLabel")}
								</Label>
								<Input
									id="homeowner-name"
									value={homeownerName}
									onChange={(e) => setHomeownerName(e.target.value)}
									placeholder={t("realtor.claim.namePlaceholder")}
									maxLength={200}
									className="border-dashboard-border bg-dashboard-bg text-dashboard-text"
								/>
							</div>
							<StudioButton
								type="submit"
								variant="primary"
								className="w-full gap-1.5"
								disabled={claimInFlight}
							>
								{claimInFlight ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<CheckCircle2 className="h-4 w-4" />
								)}
								{t("realtor.claim.complete")}
							</StudioButton>
						</CardContent>
					</Card>
				</form>
			)}

			<p
				className="text-[11px] text-center mt-4 px-5"
				style={{ color: "var(--color-dashboard-muted)" }}
			>
				{t("realtor.claim.expiresOn", {
					date: new Date(preview.expiresAt).toLocaleDateString(),
				})}
			</p>
		</div>
	);
}
